import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Filter, Search, RefreshCw } from "lucide-react";
import { gsap } from "gsap";
import { Button } from "@/components/ui/button";
import { QuotationTable } from "@/components/QuotationTable";
import { QuotationForm } from "@/components/QuotationForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, trackEvent, RealtimePayload } from "@/integrations/supabase/client";
import { 
  generateQuotationUrl, 
  generateQuotationMessage, 
  formatWhatsAppNumber, 
  openWhatsApp 
} from "@/utils/quotation-utils";

export function MobileQuotationsPage() {
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [channelName] = useState(`mobile-quotations-${Math.random().toString(36).substring(2, 10)}`);
  
  useEffect(() => {
    console.log(`MobileQuotationsPage: Setting up channel ${channelName}`);
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quotations",
        },
        async (payload: RealtimePayload) => {
          console.log("MobileQuotationsPage detected change:", payload.eventType);
          
          const eventId = payload.new?.id || payload.old?.id;
          if (!eventId) {
            console.log('MobileQuotationsPage: No event ID found, skipping');
            return;
          }
          
          const isProcessed = trackEvent(`${payload.eventType}-mobile-quotation`, eventId);
          if (isProcessed) {
            console.log(`MobileQuotationsPage: Event ${payload.eventType}-${eventId} already processed, skipping`);
            return;
          }
          
          queryClient.invalidateQueries({ queryKey: ["quotations"] });
          
          const { data } = await supabase.auth.getUser();
          const currentUserId = data.user?.id;
          
          const changeUserId = payload.new?.created_by || payload.old?.created_by;
          
          if (currentUserId !== changeUserId) {
            if (payload.eventType === 'INSERT') {
              toast.info("Novo orçamento adicionado", {
                position: "bottom-center",
                duration: 2000,
              });
            } else if (payload.eventType === 'UPDATE') {
              toast.info("Orçamento atualizado", {
                position: "bottom-center",
                duration: 2000,
              });
            } else if (payload.eventType === 'DELETE') {
              toast.info("Orçamento removido", {
                position: "bottom-center",
                duration: 2000,
              });
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('MobileQuotationsPage real-time subscription status:', status);
      });

    return () => {
      console.log(`MobileQuotationsPage: Cleaning up channel ${channelName}`);
      supabase.removeChannel(channel);
    };
  }, [queryClient, channelName]);
  
  useEffect(() => {
    if (mainRef.current && fabRef.current) {
      const tl = gsap.timeline();
      
      tl.fromTo(
        mainRef.current, 
        { opacity: 0, y: 20 }, 
        { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" }
      );
      
      tl.fromTo(
        fabRef.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, ease: "back.out(1.7)" },
        "-=0.2"
      );
    }
  }, []);

  const handleEdit = (quotation: any) => {
    setEditingQuotation(quotation);
    setShowQuotationForm(true);
  };

  const handleDelete = (quotation: any) => {
    setQuotationToDelete(quotation);
    setDeleteDialogOpen(true);
  };
  
  const handleView = (quotation: any) => {
    navigate(`/quotation/${quotation.code}`);
  };
  
  const handleShare = (quotation: any) => {
    const shareUrl = generateQuotationUrl(quotation.code);
    
    if (navigator.share) {
      navigator.share({
        title: `Orçamento #${quotation.code}`,
        text: `Orçamento #${quotation.code} para ${quotation.customer?.name}`,
        url: shareUrl,
      })
      .catch((error) => {
        console.error('Erro ao compartilhar:', error);
        
        // If native sharing fails, try WhatsApp if phone exists
        const phone = quotation.customer?.whatsapp || quotation.customer?.phone;
        if (phone) {
          const formattedPhone = formatWhatsAppNumber(phone);
          const message = generateQuotationMessage(quotation.customer.name, shareUrl);
          openWhatsApp(formattedPhone, message);
        } else {
          // Fallback to clipboard if no phone
          copyToClipboard(shareUrl);
        }
      });
    } else {
      // If Web Share API is not available
      const phone = quotation.customer?.whatsapp || quotation.customer?.phone;
      if (phone) {
        const formattedPhone = formatWhatsAppNumber(phone);
        const message = generateQuotationMessage(quotation.customer.name, shareUrl);
        openWhatsApp(formattedPhone, message);
      } else {
        copyToClipboard(shareUrl);
      }
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success("Link copiado para a área de transferência", {
          position: "bottom-center",
          duration: 2000,
        });
      })
      .catch((error) => {
        console.error("Erro ao copiar link:", error);
        toast.error("Erro ao copiar link", {
          position: "bottom-center",
          duration: 2000,
        });
      });
  };
  
  const confirmDelete = async () => {
    if (!quotationToDelete) return;
    
    try {
      const { error } = await supabase
        .from('quotations')
        .delete()
        .eq('id', quotationToDelete.id);
        
      if (error) throw error;
      
      toast.success("Orçamento excluído com sucesso", {
        position: "bottom-center",
        duration: 2000,
      });
      
      // Clear service worker cache for quotations
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_QUOTATIONS_CACHE'
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } catch (error) {
      console.error("Erro ao excluir orçamento:", error);
      toast.error("Erro ao excluir orçamento", {
        position: "bottom-center",
        duration: 2000,
      });
    } finally {
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
    }
  };

  const handleCloseForm = () => {
    if (showQuotationForm) {
      const modal = document.querySelector(".modal-container");
      if (modal) {
        gsap.to(modal, {
          y: 20,
          opacity: 0,
          duration: 0.3,
          ease: "power3.in",
          onComplete: () => {
            setShowQuotationForm(false);
            setEditingQuotation(null);
          }
        });
      } else {
        setShowQuotationForm(false);
        setEditingQuotation(null);
      }
    }
  };

  const refreshQuotations = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      
      // Clear service worker cache for quotations
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_QUOTATIONS_CACHE'
        });
      }
      
      toast.success("Dados atualizados", {
        position: "bottom-center",
        duration: 2000,
      });
    } catch (error) {
      console.error("Erro ao atualizar orçamentos:", error);
      toast.error("Erro ao atualizar", {
        position: "bottom-center",
        duration: 2000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div ref={mainRef} className="px-4 py-2 pb-20">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              ref={fabRef}
              onClick={() => setShowQuotationForm(true)}
              className="w-12 h-12 bg-[#0073B7] rounded-full flex items-center justify-center shadow-lg mr-3"
            >
              <Plus className="text-white h-6 w-6" />
            </button>
            <h2 className="text-xl font-bold text-white font-['Russo_One']">
              Orçamentos
            </h2>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full text-white hover:bg-white/10"
              onClick={refreshQuotations}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                <RefreshCw className="h-5 w-5" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full text-white hover:bg-white/10"
            >
              <Search className="h-5 w-5" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full text-white hover:bg-white/10"
            >
              <Filter className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg overflow-hidden mb-16">
        <QuotationTable 
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onShare={handleShare}
        />
      </div>

      {showQuotationForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div 
            className="modal-container w-full h-full md:h-auto md:w-[90%] md:max-w-3xl md:rounded-xl bg-[#1A1F2C] overflow-hidden shadow-xl"
          >
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white font-['Russo_One']">
                  {editingQuotation ? "Editar" : "Novo"} Orçamento
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-white/70 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <QuotationForm
                  onClose={handleCloseForm}
                  initialData={editingQuotation}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1F2C] rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Confirmar exclusão</h3>
            <p className="text-gray-300 mb-6">
              Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setQuotationToDelete(null);
                }}
                className="text-white hover:bg-gray-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-[#E60026] hover:bg-red-700 text-white"
              >
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
